import React from 'react';
import { Link } from 'react-router-dom';
import { Phone } from 'lucide-react';
import LandingShell from '../components/landing/LandingShell';
import ResultsRail from '../components/results/ResultsRail';
import ResultVideoTile from '../components/results/ResultVideoTile';
import { hasConsent } from '../components/results/ResultCard';
import { resultVideos } from '../data/results';
import { TEL_PRIMARY_HREF } from '../config/contact';
import { FOUNDING_YEAR, STUDENTS_MENTORED } from '../config/institute';

/*
 * Public marketing landing page, rendered at "/".
 *
 * Built from design-reference/Future Makers Landing.html, which ships a desktop
 * artboard (1180px content width, lg and up) and a mobile artboard (390px, base)
 * side by side. This is ONE responsive component: the mobile artboard is the base
 * layout, the desktop artboard rides on lg: variants.
 *
 * Header, footer, scroll container and the fixed mobile call bar live in
 * LandingShell (shared with /results). The shell passes scrollToSection to the
 * function child for the in-page anchor CTAs below.
 *
 * Testimonials read from the same resultVideos dataset /results uses
 * (consent-gated, ResultVideoTile) — unified the same way the results cards
 * were. This page shows the first 4 consented stories; /results#stories
 * shows all of them.
 */

const PROOF = [
  { stat: '12', label: 'students per batch, maximum' },
  { stat: `${STUDENTS_MENTORED}+`, label: `students mentored since ${FOUNDING_YEAR}` },
  { stat: 'Weekly', label: 'parent progress reports' },
  { stat: 'Hybrid', label: 'attend at the centre or online' },
];

const CATEGORIES = [
  { tag: '6–12', label: 'Classes 6–12', note: 'CBSE, ICSE & IB school support' },
  { tag: 'JEE', label: 'IIT-JEE & Olympiad', note: 'Foundation from Class 9' },
  { tag: 'NEET', label: 'NEET', note: 'Biology, Physics, Chemistry' },
  { tag: 'IPMAT', label: 'IPMAT & CAT', note: 'Aptitude + verbal ability' },
  { tag: 'CLAT', label: 'CLAT', note: 'Legal reasoning & GK' },
  { tag: 'CUET', label: 'CUET', note: 'Domain + general test' },
  { tag: 'SAT', label: 'IELTS & SAT', note: 'Study-abroad pathways' },
  { tag: 'AP', label: 'AP Exams', note: 'Calculus, Physics, Econ' },
];

const SUBJECTS = ['Math', 'Physics', 'Chemistry', 'Biology', 'Accounts', 'Economics', 'English'];

const VALUES = [
  {
    num: '01',
    title: 'Strong academic foundation',
    body: 'Deep conceptual clarity in maths, science and languages, plus the critical thinking and problem-solving that boards and entrance exams both reward.',
  },
  {
    num: '02',
    title: 'Holistic development',
    body: 'We build discipline, emotional resilience and ethical values alongside marks — so students grow up capable, not just examined.',
  },
  {
    num: '03',
    title: 'Flexible hybrid learning',
    body: 'Attend at either Gurugram centre or join the same session live online. Recordings and doubt-clearing stay available all term.',
  },
];

/* Founder block content.

   credentials is stored as segments and joined at render with a NON-BREAKING
   space before each separator, so "·" always trails the segment it follows and
   can never orphan itself at the start of a wrapped line. At 390px this line
   wraps to four lines, so the binding matters. Same reasoning as the student
   dashboard row's "•" separators (see CLAUDE.md). */
const FOUNDER = {
  name: 'Dr. Ram Sharma',
  credentials: [
    'Ph.D. and M.Tech in Electronics & Communication Engineering',
    'B.E. (1989)',
    'Former General Manager, Reliance Communications',
    `Teaching since ${FOUNDING_YEAR}`,
  ].join('\u00A0· '),  // \u00A0 = NBSP: binds the separator to the segment before it
  bio: `Dr. Ram Sharma has taught Physics and Mathematics in Gurugram since ${FOUNDING_YEAR}, working with over ${STUDENTS_MENTORED} students across CBSE, IB, and competitive entrance preparation. He caps every batch at twelve so no student is a face in a crowd. Parents reach him directly — not a counsellor, not a call centre.`,
};

// Homepage strip shows the first 4 consented stories; /results#stories shows all of them.
const homeStories = resultVideos.filter(hasConsent).slice(0, 4);

const LandingPage = () => (
  <LandingShell>
    {(scrollToSection) => (
      <>
        {/* ---------- Hero ---------- */}
        <section className="bg-gradient-to-b from-brand-deep to-brand-mid">
          <div className="mx-auto max-w-[1180px] px-5 pt-8 pb-10 lg:px-11 lg:pt-20 lg:pb-[88px] grid grid-cols-1 lg:grid-cols-[1.15fr_.85fr] gap-0 lg:gap-14 lg:items-center">
            <div>
              <span className="inline-flex items-center gap-2 px-[11px] lg:px-3 py-1.5 rounded-full bg-brand-amber/[0.16] text-[10.5px] lg:text-[11.5px] font-semibold text-brand-amber-light tracking-[0.04em] mb-[18px] lg:mb-6">
                <span className="lg:hidden">Gurugram · Classes 6–12</span>
                <span className="hidden lg:inline">Gurugram · 2 centres · Classes 6–12</span>
              </span>
              <h1 className="text-[34px] lg:text-[58px] font-extrabold leading-[1.1] lg:leading-[1.05] text-white tracking-[-0.025em] lg:tracking-[-0.03em] mb-[14px] lg:mb-5">
                The coaching that actually knows your child.
              </h1>
              <p className="text-[15px] lg:text-[17.5px] leading-relaxed text-white/[0.74] lg:text-white/[0.76] lg:max-w-[48ch] mb-[22px] lg:mb-8">
                Concept-first teaching in Math, Physics, Chemistry, Biology, Accounts, Economics and
                English — plus focused JEE, NEET, IPMAT, CLAT and CUET preparation, in small
                accountable batches.
              </p>
              <div className="flex flex-col lg:flex-row gap-[10px] lg:gap-3 lg:items-center">
                <a
                  href="#enroll"
                  onClick={(e) => scrollToSection(e, 'enroll')}
                  className="px-4 lg:px-7 py-[15px] rounded-[10px] bg-brand-amber text-[15px] font-bold text-brand-deep text-center"
                >
                  Enroll Now
                </a>
                <a
                  href={TEL_PRIMARY_HREF}
                  className="px-4 lg:px-[26px] py-[15px] rounded-[10px] border border-white/[0.32] text-[15px] font-semibold text-white text-center"
                >
                  Call Dr. Ram Sharma
                </a>
              </div>
            </div>

            {/* Proof: stacked stat rows in a card on desktop, 2-col grid under the CTAs on mobile */}
            <div className="mt-[26px] pt-[22px] border-t border-white/[0.14] lg:mt-0 lg:pt-7 lg:border-t-0 lg:border lg:border-white/[0.16] lg:bg-white/[0.07] lg:rounded-2xl lg:px-7 lg:pb-7">
              <span className="hidden lg:block text-xs font-bold text-white/55 tracking-[0.1em] mb-5">
                WHY PARENTS CHOOSE US
              </span>
              <div className="grid grid-cols-2 gap-x-3 gap-y-[18px] lg:grid-cols-1 lg:gap-0">
                {PROOF.map((item) => (
                  <div
                    key={item.label}
                    className="lg:flex lg:gap-4 lg:items-center lg:py-3.5 lg:border-b lg:border-white/[0.12]"
                  >
                    <span className="block text-[22px] lg:text-[28px] font-extrabold leading-none text-brand-amber lg:min-w-[78px]">
                      {item.stat}
                    </span>
                    <span className="block mt-[5px] lg:mt-0 text-[12.5px] lg:text-sm leading-snug lg:leading-relaxed text-white/70 lg:text-white/[0.82]">
                      {item.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ---------- Featured results rail ---------- */}
        <ResultsRail />

        {/* ---------- Founder ---------- */}
        <section className="bg-white">
          <div className="mx-auto max-w-[1180px] px-5 py-9 lg:px-11 lg:py-[76px] grid grid-cols-1 lg:grid-cols-[.7fr_1.3fr] gap-6 lg:gap-14 lg:items-center">
            {/* Portrait — 900x1125 (4:5), matches the box exactly.
                No border and no rounding by request: the photo's own background is
                white and the section is white, so the two meet with no visible edge.
                Anything framing it reappears as a box around his head. */}
            <div className="relative aspect-[4/5] w-full max-w-[300px] lg:max-w-none overflow-hidden">
              <img
                src="/assets/founder-ram-sharma.webp"
                alt={FOUNDER.name}
                className="w-full h-full object-cover"
              />
            </div>

            <div>
              <span className="block text-[10.5px] lg:text-[11.5px] font-bold text-brand-faint tracking-[0.12em] mb-2 lg:mb-3">
                MEET THE FOUNDER
              </span>
              <h2 className="text-[25px] lg:text-[38px] font-extrabold leading-tight text-brand-deep tracking-[-0.02em] lg:tracking-[-0.025em] mb-1.5 lg:mb-2">
                {FOUNDER.name}
              </h2>
              {/* Resolved credentials — normal body text, not the mono
                  placeholder treatment this carried while unresolved. Shares
                  the bio's measure so the two paragraphs wrap on the same
                  column. */}
              <p className="text-[13px] lg:text-[14.5px] leading-relaxed text-brand-muted mb-3 lg:mb-4 lg:max-w-[58ch]">
                {FOUNDER.credentials}
              </p>
              <p className="text-[13.5px] lg:text-[15.5px] leading-relaxed text-brand-slate mb-5 lg:mb-7 lg:max-w-[58ch]">
                {FOUNDER.bio}
              </p>
              <a
                href={TEL_PRIMARY_HREF}
                className="inline-flex items-center gap-2 px-4 lg:px-7 py-[15px] rounded-[10px] bg-brand-amber text-[15px] font-bold text-brand-deep"
              >
                <Phone className="w-[18px] h-[18px]" />
                Call Dr. Ram Sharma
              </a>
            </div>
          </div>
        </section>

        {/* ---------- Exam prep (#exams) ---------- */}
        <section id="exams" className="scroll-mt-16 lg:scroll-mt-20 bg-brand-mist">
          <div className="mx-auto max-w-[1180px] px-5 py-9 lg:px-11 lg:py-[76px]">
            <div className="lg:text-center lg:mb-10">
              <h2 className="text-[25px] lg:text-[38px] font-extrabold leading-tight text-brand-deep tracking-[-0.02em] lg:tracking-[-0.025em] mb-1.5 lg:mb-2.5">
                What are you preparing for?
              </h2>
              <p className="text-sm lg:text-[15.5px] text-brand-slate mb-5 lg:mb-0">
                <span className="lg:hidden">Eight tracks, mapped to your school calendar.</span>
                <span className="hidden lg:inline">
                  Eight tracks, one syllabus plan built around your school calendar.
                </span>
              </p>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 lg:gap-4">
              {CATEGORIES.map((cat) => (
                <a
                  key={cat.tag}
                  href="#programs"
                  onClick={(e) => scrollToSection(e, 'programs')}
                  className="bg-white border border-brand-line rounded-xl lg:rounded-[14px] p-3.5 lg:p-[22px] flex flex-col gap-[7px] lg:gap-2.5 hover:border-brand-deep hover:shadow-[0_6px_20px_rgba(11,58,69,.1)] transition-all"
                >
                  <span className="inline-flex items-center justify-center self-start min-w-[42px] lg:min-w-[46px] h-[26px] lg:h-[30px] px-2 lg:px-[9px] rounded-[7px] lg:rounded-lg bg-brand-tint text-brand-deep text-[11px] lg:text-xs font-bold">
                    {cat.tag}
                  </span>
                  <span className="text-sm lg:text-base font-bold leading-tight text-brand-deep">
                    {cat.label}
                  </span>
                  <span className="text-[11.5px] lg:text-[13px] leading-snug text-brand-muted">
                    {cat.note}
                  </span>
                </a>
              ))}
            </div>

            <div className="mt-4 lg:mt-[26px] flex flex-wrap gap-[7px] lg:gap-2 lg:justify-center">
              {SUBJECTS.map((subject) => (
                <span
                  key={subject}
                  className="px-3 lg:px-3.5 py-1.5 lg:py-[7px] rounded-full bg-white border border-brand-line text-xs lg:text-[13px] font-medium text-brand-ink"
                >
                  {subject}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* ---------- Programs (#programs) ---------- */}
        <section id="programs" className="scroll-mt-16 lg:scroll-mt-20 bg-white">
          <div className="mx-auto max-w-[1180px] px-5 py-9 lg:px-11 lg:py-[76px]">
            <h2 className="text-[25px] lg:text-[38px] font-extrabold leading-tight text-brand-deep tracking-[-0.02em] lg:tracking-[-0.025em] mb-1.5 lg:mb-2">
              What you get with us
            </h2>
            <p className="text-sm lg:text-[15.5px] text-brand-slate mb-5 lg:mb-9">
              <span className="lg:hidden">Not just marks — habits and confidence.</span>
              <span className="hidden lg:inline">
                Not just marks — habits, confidence and a way of thinking.
              </span>
            </p>

            <div className="flex flex-col gap-3 lg:grid lg:grid-cols-3 lg:gap-[22px]">
              {VALUES.map((value) => (
                <div
                  key={value.num}
                  className="border border-brand-line rounded-[14px] lg:rounded-2xl overflow-hidden flex flex-col"
                >
                  <div className="h-[5px] lg:h-1.5 bg-brand-amber" />
                  <div className="p-[18px] lg:p-7 flex flex-col gap-2 lg:gap-3">
                    <span className="text-[10.5px] lg:text-[11.5px] font-bold text-brand-faint tracking-[0.12em]">
                      {value.num}
                    </span>
                    <h3 className="text-[18px] lg:text-[21px] font-bold leading-tight text-brand-deep tracking-[-0.015em]">
                      {value.title}
                    </h3>
                    <p className="text-[13.5px] lg:text-[14.5px] leading-relaxed text-brand-slate">
                      {value.body}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ---------- Testimonials (#stories) ---------- */}
        <section id="stories" className="scroll-mt-16 lg:scroll-mt-20 bg-brand-deep">
          <div className="mx-auto max-w-[1180px] py-9 pl-5 lg:px-11 lg:py-[76px]">
            <div className="lg:flex lg:items-end lg:justify-between lg:mb-[34px] pr-5 lg:pr-0">
              <div>
                <h2 className="text-[25px] lg:text-[38px] font-extrabold leading-tight text-white tracking-[-0.02em] lg:tracking-[-0.025em] mb-1.5 lg:mb-2">
                  Students speak
                </h2>
                <p className="text-sm lg:text-[15px] text-white/65 mb-5 lg:mb-0">
                  <span className="lg:hidden">Swipe — unscripted, filmed at Sector 49.</span>
                  <span className="hidden lg:inline">Unscripted, filmed at our Sector 49 centre.</span>
                </p>
              </div>
              <Link
                to="/stories"
                className="hidden lg:inline-block px-5 py-[11px] rounded-[9px] border border-white/30 text-[13.5px] font-semibold text-white"
              >
                Watch all stories
              </Link>
            </div>

            {/* Mobile: horizontal swipe. Desktop: 4-up grid. */}
            <div className="flex gap-3 overflow-x-auto pr-5 pb-1.5 lg:grid lg:grid-cols-4 lg:gap-5 lg:overflow-visible lg:pr-0 lg:pb-0">
              {homeStories.map((entry) => (
                <div key={entry.slug} className="flex-none w-[172px] lg:w-auto">
                  <ResultVideoTile entry={entry} />
                </div>
              ))}
            </div>
          </div>
        </section>
      </>
    )}
  </LandingShell>
);

export default LandingPage;
